/**
 * Consultation API Client
 * Handles communication with the Laravel backend for consultation booking
 */

export class ConsultationAPI {
  #baseUrl;
  #locale;
  #slotsCache = null;
  #slotsCacheTime = 0;
  #cacheTimeout = 2 * 60 * 1000; // 2 minutes (slots change more frequently)

  constructor(baseUrl, locale = 'cs') {
    this.#baseUrl = baseUrl.replace(/\/$/, '');
    this.#locale = locale;
  }

  async #request(endpoint, options = {}) {
    const url = `${this.#baseUrl}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...options.headers,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Příliš mnoho požadavků. Zkuste to prosím za chvíli.');
        }
        let errorMessage = 'Něco se pokazilo. Zkuste to prosím znovu.';
        try {
          const data = await response.json();
          errorMessage = data.error || data.message || errorMessage;
        } catch {
          // Response body is not JSON — use the default message
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get available consultation slots.
   * @returns {Promise<{slots: Array<{date: string, times: string[]}>, settings: Object}>}
   */
  async getSlots() {
    const now = Date.now();
    if (this.#slotsCache && (now - this.#slotsCacheTime) < this.#cacheTimeout) {
      return this.#slotsCache;
    }

    const data = await this.#request('/consultation-slots');
    this.#slotsCache = data;
    this.#slotsCacheTime = now;
    return data;
  }

  /**
   * Invalidate slots cache (after creating a reservation).
   */
  invalidateSlotsCache() {
    this.#slotsCache = null;
    this.#slotsCacheTime = 0;
  }

  /**
   * Create a soft-lock reservation.
   * @param {Object} data - { date, time, phone, email? }
   * @returns {Promise<{token: string, date: string, time: string, expires_at: string}>}
   */
  async createReservation(data) {
    this.invalidateSlotsCache();
    return this.#request('/consultation-reservations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Cancel a pending reservation.
   * @param {string} token
   */
  async cancelReservation(token) {
    this.invalidateSlotsCache();
    return this.#request(`/consultation-reservations/${token}`, {
      method: 'DELETE',
    });
  }
}

// Singleton
let apiInstance = null;

export function getConsultationAPI(baseUrl, locale) {
  if (!apiInstance && baseUrl) {
    apiInstance = new ConsultationAPI(baseUrl, locale);
  }
  return apiInstance;
}
